import { DASHBOARD_WIDGET_KINDS, DASHBOARD_WIDGET_STATUSES } from "./dashboard-widget-values";
import { z } from "zod";

import { canAccessProtectedAppPath, canViewFinancials } from "../platform/access-control";
import { APP_PATHS, APP_ROUTES } from "../platform/app-metadata";
import { TENANT_CURRENCY } from "../values/currency";
import catalogJson from "./dashboard-catalog.json";
import { DASHBOARD_AREA_KEYS, type DashboardAreaKey } from "./dashboard-areas";
import { DASHBOARD_COPY, dashboardOrderDateFieldSchema } from "./dashboard-copy";
import { fillTemplate } from "../values/fill-template";
import type { DashboardWidgetData } from "./dashboard-data";
import { dateOnlyKey } from "../values/date-only";

export { dashboardWidgetDataSchema, type DashboardWidgetData } from "./dashboard-data";

const PERIOD_KEYS = ["thisMonth", "lastMonth", "yearToDate", "lastYear", "last12Months", "allTime", "custom"] as const;
export type DashboardPeriodKey = (typeof PERIOD_KEYS)[number];

const POINT_LABEL_KINDS = ["contactType", "role", "transactionType", "documentType", "area", "activityAction", "yearDirection"] as const;
export type DashboardPointLabelKind = (typeof POINT_LABEL_KINDS)[number];

const rawWidgetSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum([DASHBOARD_WIDGET_KINDS.stat, DASHBOARD_WIDGET_KINDS.bar]),
  unit: z.enum(["count", "quantity", "money"]),
  timing: z.enum(["snapshot", "period"]),
  orderDateField: dashboardOrderDateFieldSchema.optional(),
  financial: z.boolean(),
  definition: z.string().min(1),
  pointLabels: z.enum(POINT_LABEL_KINDS).optional(),
  limit: z.number().int().positive().optional(),
});

function withFilledDefinition(widget: z.infer<typeof rawWidgetSchema>) {
  const period = widget.orderDateField
    ? fillTemplate(DASHBOARD_COPY.orderPeriodDefinition, { date: DASHBOARD_COPY.orderDates[widget.orderDateField] }) : "";
  const definition = fillTemplate(widget.definition, { currency: TENANT_CURRENCY, limit: widget.limit });
  return { ...widget, definition: [definition, period].filter(Boolean).join(" ") };
}

const widgetSchema = rawWidgetSchema.transform(withFilledDefinition);

function areaFromPage({ page, label, widgets }: { page: DashboardAreaKey; label?: string; widgets: DashboardWidget[] }) {
  return { key: page, label: label ?? APP_ROUTES[page].navLabel ?? APP_ROUTES[page].pageTitle, widgets };
}

const areaSchema = z.object({ page: z.enum(DASHBOARD_AREA_KEYS), label: z.string().min(1).optional(), widgets: z.array(widgetSchema).min(1) }).transform(areaFromPage);

function sameOrder(actual: readonly string[], expected: readonly string[]): boolean {
  return actual.length === expected.length && actual.every((value, index) => value === expected[index]);
}

const catalogSchema = z
  .object({
    areas: z.array(areaSchema),
    defaultPeriod: z.enum(PERIOD_KEYS),
    periods: z.array(z.object({ key: z.enum(PERIOD_KEYS), label: z.string() })),
  })
  .refine((catalog) => sameOrder(catalog.areas.map((area) => area.key), DASHBOARD_AREA_KEYS), { message: DASHBOARD_COPY.catalogErrors.areasOrder })
  .refine((catalog) => sameOrder(catalog.periods.map((period) => period.key), PERIOD_KEYS), { message: DASHBOARD_COPY.catalogErrors.periodsOrder });

export type DashboardWidget = z.output<typeof widgetSchema>;
export type DashboardArea = z.output<typeof areaSchema>;

export function parseDashboardCatalog(json: unknown) {
  return catalogSchema.parse(json);
}

const CATALOG = parseDashboardCatalog(catalogJson);

export const DASHBOARD_AREAS: readonly DashboardArea[] = CATALOG.areas;
export const DASHBOARD_PERIODS = CATALOG.periods;
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriodKey = CATALOG.defaultPeriod;

export function dashboardWidget(key: string): DashboardWidget {
  return DASHBOARD_AREAS.flatMap((area) => area.widgets).find((widget) => widget.key === key)!;
}

export function dashboardAreaLabel(key: DashboardAreaKey): string {
  return DASHBOARD_AREAS.find((area) => area.key === key)!.label;
}

export function dashboardAreasForRole(role: string | null | undefined): DashboardArea[] {
  return DASHBOARD_AREAS.filter((area) => canAccessProtectedAppPath(role, APP_PATHS[area.key]));
}

export function dashboardWidgetsForRole(area: DashboardArea, role: string | null | undefined): DashboardWidget[] {
  return canViewFinancials(role) ? area.widgets : area.widgets.filter((widget) => !widget.financial);
}

export type DashboardPeriodSelection =
  | { period: Exclude<DashboardPeriodKey, "custom"> }
  | { period: "custom"; from: string; to: string };

export type DashboardDateRange = { start: string; endExclusive: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day));
}

function isoDay(date: Date): string {
  return dateOnlyKey(date);
}

export function utcDayStartMs(isoDay: string): number {
  return Date.parse(`${isoDay}T00:00:00.000Z`);
}

function parseIsoDay(value: string): Date | null {
  if (!ISO_DATE.test(value)) return null;
  const date = new Date(utcDayStartMs(value));
  return isoDay(date) === value ? date : null;
}

function addDays(date: Date, days: number): Date {
  return utcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days);
}

function range(start: Date, endExclusive: Date): DashboardDateRange {
  return { start: isoDay(start), endExclusive: isoDay(endExclusive) };
}

type PresetResolver = (today: Date, tomorrow: Date) => DashboardDateRange;

export const ALL_TIME_START_BEFORE_ANY_RECORD = "1970-01-01";

const PRESET_RANGES: Record<Exclude<DashboardPeriodKey, "custom">, PresetResolver> = {
  thisMonth: (today, tomorrow) => range(utcDate(today.getUTCFullYear(), today.getUTCMonth(), 1), tomorrow),
  lastMonth: (today) => range(utcDate(today.getUTCFullYear(), today.getUTCMonth() - 1, 1), utcDate(today.getUTCFullYear(), today.getUTCMonth(), 1)),
  yearToDate: (today, tomorrow) => range(utcDate(today.getUTCFullYear(), 0, 1), tomorrow),
  lastYear: (today) => range(utcDate(today.getUTCFullYear() - 1, 0, 1), utcDate(today.getUTCFullYear(), 0, 1)),
  last12Months: (_today, tomorrow) => range(utcDate(tomorrow.getUTCFullYear() - 1, tomorrow.getUTCMonth(), tomorrow.getUTCDate()), tomorrow),
  allTime: (_today, tomorrow) => ({ start: ALL_TIME_START_BEFORE_ANY_RECORD, endExclusive: isoDay(tomorrow) }),
};

export function resolveDashboardPeriod(selection: DashboardPeriodSelection, now: Date): DashboardDateRange {
  if (selection.period === "custom") return range(parseIsoDay(selection.from)!, addDays(parseIsoDay(selection.to)!, 1));
  const today = utcDate(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return PRESET_RANGES[selection.period](today, addDays(today, 1));
}

export function customRangeError(from: string, to: string, now: Date): string | null {
  const start = parseIsoDay(from);
  const end = parseIsoDay(to);
  if (!start) return DASHBOARD_COPY.periodErrors.missingStart;
  if (!end) return DASHBOARD_COPY.periodErrors.missingEnd;
  if (start > end) return DASHBOARD_COPY.periodErrors.reversed;
  return isoDay(end) > isoDay(now) ? DASHBOARD_COPY.periodErrors.future : null;
}

export type DashboardWidgetState =
  | { status: typeof DASHBOARD_WIDGET_STATUSES.loading }
  | { status: typeof DASHBOARD_WIDGET_STATUSES.error }
  | { status: typeof DASHBOARD_WIDGET_STATUSES.empty }
  | { status: typeof DASHBOARD_WIDGET_STATUSES.ready; data: DashboardWidgetData };

export const dashboardPeriodDraftSchema = z.object({ period: z.enum(PERIOD_KEYS), from: z.string(), to: z.string() });
export type DashboardPeriodDraft = z.infer<typeof dashboardPeriodDraftSchema>;

export type DashboardPeriodState = { label: string; range: DashboardDateRange | null; error: string | null };

function periodLabel(period: DashboardPeriodKey): string {
  return DASHBOARD_PERIODS.find((entry) => entry.key === period)!.label;
}

function customPeriodState({ from, to }: DashboardPeriodDraft, now: Date): DashboardPeriodState {
  const error = customRangeError(from, to, now);
  if (error) return { label: periodLabel("custom"), range: null, error };
  return { label: fillTemplate(DASHBOARD_COPY.customPeriod, { from, to }), range: resolveDashboardPeriod({ period: "custom", from, to }, now), error: null };
}

export function dashboardPeriodState(draft: DashboardPeriodDraft, now: Date): DashboardPeriodState {
  if (draft.period === "custom") return customPeriodState(draft, now);
  return { label: periodLabel(draft.period), range: resolveDashboardPeriod({ period: draft.period }, now), error: null };
}
